<template>
	<feature-item :image="product.image" :title="product.name" :subtitle="$t('label.supplied_by_name', {name: product.company.name})" :description="product.description" divided>

		<a v-if="product.product_link" :href="product.product_link" target="_blank" rel="noopener" class="link text-sm flex items-center mt-16">
			<icon class="mr-5" x-small :icon="icons.external_link"></icon>
			<span>{{ $t('text.finisherpoints.product_info') }}</span>
		</a>

		<template #info>
			<div class="flex flex-column">
				<p class="text-xl leading-normal mb-0 text-right" :class="[purchasable || !purchaseEnabled ? 'text-success':'text-warning']">{{ product.price }}
					{{ $t('abbreviation.points') }}</p>
				<p class="text-2xs mb-0 text-right" :class="[product.stock > 5 ? 'text-gray-400':'warning']">{{
						$t('param_label.in_stock', product.stock, {count: product.stock})
					}}</p>
			</div>
		</template>

		<template #actions>
			<div class="mt-16 text-right">
				<btn v-if="purchasable" color="success" @click.prevent="showPurchaseForm">
					<icon :icon="icons.product"></icon>
					<span>{{ $t('action.finisherpoints_product.select') }}</span>
				</btn>
				<btn v-else-if="purchaseEnabled" disabled color="success">
					<icon :icon="icons.product"></icon>
					<span>{{ $t('label.more_points_needed', {points: product.price - $auth.user.finisherpoints}) }}</span>
				</btn>
			</div>
		</template>

		<lazy-ajax-form-modal v-if="purchasable"
							  v-model="showPurchaseModal"
							  :form-options="{action: $api('users.finisherpoints.purchases.store', [$auth.user._key, product._key]), data: purchaseData, method: 'POST'}"
							  :modal-options="{title: $t('action.finisherpoints_product.select'), buttonBreakColumn: 'xs'}"
							  :submit-text="$t('action.finisherpoints_product.select_with_points', {points: purchasePoints})"
							  @success="handlePurchasedProduct">

			<div class="flex flex-column sm:flex-row">
				<div v-if="product.image">
					<cdn-image :src="product.image" class="md:mr-10" :size="{width: 140, ratio: 1}" rounded/>
				</div>

				<lazy-card-text class="flex flex-column py-8 sm:py-0">
					<div class="flex flex-column mb:10">
						<p class="font-heading text-text-color font-bold text-lg mb-2">{{ product.name }}</p>
						<p class="text-2xs flex mb-10">
							<span class="meta">{{ $t('label.supplied_by_name', {name: product.company.name}) }}</span>
						</p>
						<p class="text-subtext-color text-sm">{{ product.description }}</p>
					</div>
					<div class="flex flex-column">
						<p class="text-xl mb-0 leading-normal" :class="[purchasable ? 'text-success':'text-warning']">{{ product.price }} {{ $t('abbreviation.points') }}</p>
						<p class="text-2xs mb-0" :class="[product.stock > 5 ? 'text-gray-400':'warning']">{{
								$t('param_label.in_stock', product.stock, {count: product.stock})
							}}</p>
					</div>
				</lazy-card-text>
			</div>

			<divider margin="16"/>

			<template v-if="product.requires_delivery_address">
				<lazy-form-select name="quantity" v-model="purchaseData.quantity" :label="$t('label.quantity')"
								  :rules="{required: true}"
								  :options="maxSelectableQuantity"
								  :help="$t('help.finisherpoints_product_purchase.quantity')"/>

				<p class="subheading mb-16">{{ $t('label.delivery_address') }}</p>
				<lazy-form-input v-model="purchaseData.name"
								 name="name"
								 :rules="{required: true}"
								 :label="$t('label.name')"/>
				<lazy-contact-form-inputs :data="purchaseData" required :street-required="true"></lazy-contact-form-inputs>
			</template>

			<lazy-form-checkbox name="gtc"
								v-model="purchaseData.gtc"
								:rules="{accepted: true}">
				<div>
					<span>{{ $t('input.finisherpoints_product_purchase.gtc', {points: purchasePoints}) }}</span>
					<br>
					<span>
                       <a href="#" @click.prevent="showGtcModal">{{ $t('action.open_gtc') }}</a>
                    </span>
				</div>
			</lazy-form-checkbox>

		</lazy-ajax-form-modal>

	</feature-item>
</template>

<script>
import {mdiGift} from '@mdi/js';
import {externalLinkIcon, helpIcon} from "@spoferan/nuxt-spoferan/icons";
import {useGlobalStore} from "@spoferan/nuxt-spoferan/store";
import { mapActions } from 'pinia'

export default {

	name: 'FinisherPointsProductCard',

	emits: ['purchased'],

	props: {

		// The product to show
		product: {
			type: Object,
			required: true
		}
	},

	data() {
		return {
			showPurchaseModal: false,
			purchaseData: {
				quantity: 1,
				name: this.$auth.user ? this.$auth.user.display_name : null,
				country_code: this.$auth.user ? this.$auth.user.country_code : null,
				state_code: this.$auth.user ? this.$auth.user.state_code : null,
				city: this.$auth.user ? this.$auth.user.city : null,
				postcode: this.$auth.user ? this.$auth.user.postcode : null,
				street: this.$auth.user ? this.$auth.user.street : null
			},

			icons: {
				product: mdiGift,
				help: helpIcon,
				external_link: externalLinkIcon
			}
		}
	},

	computed: {

		purchaseEnabled() {
			return !!this.$auth.user;
		},

		// States if the user can purchase the product regarding his points balance.
		purchasable() {
			return this.purchaseEnabled && this.$auth.user.finisherpoints >= this.product.price
		},

		// The max selectable quantity that can be purchased
		maxSelectableQuantity() {
			if (!this.purchaseEnabled) {
				return 0;
			}

			return Math.min(10, Math.floor(this.$auth.user.finisherpoints / this.product.price))
		},

		// The points needed to purchase the product with the select quantity
		purchasePoints() {
			return this.product.price * this.purchaseData.quantity
		}
	},

	methods: {
		...mapActions(useGlobalStore, ['showGtcModal']),

		showPurchaseForm() {
			this.showPurchaseModal = true
		},
		handlePurchasedProduct(purchase) {
			this.showPurchaseModal = false
			this.$emit('purchased', purchase)
		}
	}
}
</script>